/**
 * Weighbridge files mapped onto an import template (FR-TRV-27, FR-COL-08; Technical
 * Specifications 19.2): a weighbridge exports its weighings as CSV or Excel, with its own
 * headers, number and date formats. The user maps its columns onto the template once per site;
 * the mapping turns each line into the cells the template reads, so that the rows are checked
 * as those of an Excel template. The API reads the file; everything here is pure.
 */
import { z } from 'zod';
import { IMPORT_MAX_ROWS, importColumnNames, normalizeText, parseImportDate, } from './imports.js';
export const WEIGHT_UNITS = ['KG', 'T'];
/** The decimal mark of the file's numbers; "auto" reads it from each value. */
export const DECIMAL_SEPARATORS = ['auto', ',', '.'];
export const DATE_ORDERS = ['DMY', 'MDY', 'YMD'];
/** The value of a product that the mapping leaves out of the import. */
export const IGNORED_PRODUCT = 'IGNORE';
const uuid = z.string().uuid();
export const importMappingSchema = z.object({
    /** The source header read for each template column, by column key. */
    columns: z.record(z.string(), z.string().min(1).max(200)),
    options: z
        .object({
        weightUnit: z.enum(WEIGHT_UNITS).default('KG'),
        decimalSeparator: z.enum(DECIMAL_SEPARATORS).default('auto'),
        dateOrder: z.enum(DATE_ORDERS).default('DMY'),
    })
        .default({}),
    /** The site waste of each product value (normalised), or IGNORE. */
    values: z.record(z.string(), z.union([uuid, z.literal(IGNORED_PRODUCT)])).default({}),
    /** What a line takes when the file gives nothing (stock entries). */
    defaults: z
        .object({
        wasteId: uuid.optional(),
        originId: uuid.optional(),
        conditionnementId: uuid.optional(),
    })
        .default({}),
});
/** POST /imports/:id/actions/map: the mapping to apply, and whether the site keeps it. */
export const importMapSchema = z.object({
    mapping: importMappingSchema,
    save: z.boolean().default(true),
});
// ---- values ------------------------------------------------------------------------------------
const UNIT_WORDS = { KG: /kgs?$/i, T: /(t|tonnes?)$/i };
/**
 * In "auto", the decimal mark a value shows: the last of a comma and a point when it has both;
 * a mark written twice or more groups thousands, and so does one before three digits of
 * kilograms (a weighbridge weighs whole kilograms, while tonnes keep three decimals).
 */
function decimalMark(s, unit) {
    const comma = s.lastIndexOf(',');
    const point = s.lastIndexOf('.');
    if (comma >= 0 && point >= 0)
        return comma > point ? ',' : '.';
    const mark = comma >= 0 ? ',' : point >= 0 ? '.' : null;
    if (!mark)
        return null;
    const other = mark === ',' ? '.' : ',';
    if (s.indexOf(mark) !== s.lastIndexOf(mark))
        return other;
    return unit === 'KG' && /^-?\d{1,3}[.,]\d{3}$/.test(s) ? other : mark;
}
/** A value as a number is read: spaces (non-breaking ones too) and the unit dropped. */
const numberText = (raw, unit) => {
    const s = raw.replace(/[\s  ]/g, '');
    return unit ? s.replace(UNIT_WORDS[unit], '') : s;
};
/**
 * The decimal mark the values of a file show, when those that tell agree: "8220,000" or "420,5"
 * tell, "820,000" does not (one mark before three digits). Read with it, "820,000" is 820 in
 * the file of the first two, rather than 820,000.
 */
export function fileDecimalMark(values, unit) {
    const marks = new Set();
    for (const raw of values) {
        const s = numberText(raw, unit);
        if (!/^-?\d[\d.,]*$/.test(s) || /^-?\d{1,3}[.,]\d{3}$/.test(s))
            continue;
        const mark = decimalMark(s);
        if (mark)
            marks.add(mark);
    }
    return marks.size === 1 ? [...marks][0] : null;
}
/**
 * A number as weighbridges write it: spaces (non-breaking ones too) and the unit dropped, then
 * read with the decimal mark chosen, or the one each value shows: "1 234,5", "1.234,5" and
 * "1,234.5" all read 1234.5. Undefined when it is not a number.
 */
export function normalizeImportNumber(raw, decimal = 'auto', unit) {
    const s = numberText(raw, unit);
    if (!/^-?\d[\d.,]*$/.test(s))
        return undefined;
    const mark = decimal === 'auto' ? decimalMark(s, unit) : decimal;
    if (!mark)
        return /^-?\d+$/.test(s) ? Number(s) : undefined;
    const thousands = mark === ',' ? '.' : ',';
    const esc = (c) => (c === '.' ? '\\.' : c);
    const pattern = new RegExp(`^-?(\\d+|\\d{1,3}(${esc(thousands)}\\d{3})+)(${esc(mark)}\\d+)?$`);
    if (!pattern.test(s))
        return undefined;
    return Number(s.split(thousands).join('').replace(mark, '.'));
}
const INSTANT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/i;
const pad = (n) => String(n).padStart(2, '0');
/**
 * A date or date-time as weighbridges write it, as YYYY-MM-DD: "31/10/2026 14:05", "31/10/26",
 * "2026-10-31T14:05:00", day first unless the order chosen says otherwise. The time goes; a
 * time with an offset (or Z) is an instant, dated in the site's time zone. Undefined when it is
 * not a date.
 */
export function normalizeImportDate(raw, order = 'DMY', timeZone = 'Africa/Casablanca') {
    const s = raw.trim();
    if (INSTANT.test(s)) {
        const at = new Date(s.replace(' ', 'T'));
        if (Number.isNaN(at.getTime()))
            return undefined;
        return new Intl.DateTimeFormat('en-CA', { timeZone }).format(at);
    }
    const parts = (s.split(/[T\s]/)[0] ?? '').split(/[/.-]/);
    if (parts.length !== 3 || parts.some((x) => !/^\d{1,4}$/.test(x)))
        return undefined;
    const [a, b, c] = parts.map(Number);
    // a year written first reads the same in every order
    const [y, m, d] = parts[0].length === 4 || order === 'YMD' ? [a, b, c] : order === 'MDY' ? [c, a, b] : [c, b, a];
    return parseImportDate(`${y < 100 ? 2000 + y : y}-${pad(m)}-${pad(d)}`);
}
/** The Latin letter of each Arabic letter of Moroccan plates (BR-25). */
const PLATE_LETTERS = {
    ا: 'A',
    أ: 'A',
    إ: 'A',
    آ: 'A',
    ب: 'B',
    د: 'D',
    ه: 'H',
    ة: 'H',
    و: 'W',
    ط: 'T',
    ج: 'J',
};
/**
 * A plate number reduced for comparison: upper case, without separators, the Arabic letters of
 * Moroccan plates as their Latin letters and Arabic-Indic digits as digits, so that
 * "12345-أ-6", "12345 | أ | 6" and "12345 A 6" are the same plate.
 */
export function plateKey(plate) {
    return [...plate.toUpperCase()]
        .map((ch) => {
        const code = ch.charCodeAt(0);
        if (code >= 0x660 && code <= 0x669)
            return String(code - 0x660);
        if (code >= 0x6f0 && code <= 0x6f9)
            return String(code - 0x6f0);
        return PLATE_LETTERS[ch] ?? ch;
    })
        .join('')
        .replace(/[^\p{L}\p{N}]|ـ/gu, '');
}
// ---- mapping -----------------------------------------------------------------------------------
/**
 * The mapping to start from: the saved one's columns whose headers the file has, then each other
 * column of the template under the header that names it (key, labels or aliases, as an Excel
 * template's headers are matched), with the saved options, values and defaults.
 */
export function suggestImportMapping(template, headers, saved) {
    const columns = {};
    for (const [key, header] of Object.entries(saved?.columns ?? {})) {
        if (headers.includes(header) && template.columns.some((c) => c.key === key)) {
            columns[key] = header;
        }
    }
    const taken = new Set(Object.values(columns));
    for (const column of template.columns) {
        if (columns[column.key])
            continue;
        const names = importColumnNames(column);
        const header = headers.find((h) => !taken.has(h) && names.has(normalizeText(h)));
        if (header) {
            columns[column.key] = header;
            taken.add(header);
        }
    }
    return importMappingSchema.parse({ ...saved, columns });
}
/** Whether a saved mapping reads a file: the file has every header it maps. */
export function importMappingFits(mapping, headers) {
    const mapped = Object.values(mapping.columns);
    return mapped.length > 0 && mapped.every((h) => headers.includes(h));
}
/**
 * The lines of a source as a template reads them (readImportRow): the mapped columns only, their
 * numbers and dates normalised; a value that cannot be read stays as written, so that its column
 * reports it. Blank lines are skipped; a required column left unmapped is an error of the file.
 */
export function applyImportMapping(template, source, mapping, timeZone) {
    const issues = [];
    const index = {};
    const headers = {};
    for (const column of template.columns) {
        const header = mapping.columns[column.key];
        const at = header === undefined ? -1 : source.headers.indexOf(header);
        if (at >= 0) {
            index[column.key] = at;
            headers[column.key] = header;
        }
        else if (column.required) {
            issues.push({
                row: null,
                column: column.key,
                code: 'MISSING_COLUMN',
                params: { header: column.label.fr, headerEn: column.label.en },
                severity: 'ERROR',
            });
        }
    }
    const { weightUnit, dateOrder } = mapping.options;
    // in "auto", the mark the file's weights show reads the values that do not show one
    const numbers = template.columns.filter((c) => c.type === 'number' && index[c.key] !== undefined);
    const decimalSeparator = mapping.options.decimalSeparator === 'auto'
        ? (fileDecimalMark(source.rows.flatMap((line) => numbers
            .map((c) => line.cells[index[c.key]])
            .filter((v) => typeof v === 'string')), weightUnit) ?? 'auto')
        : mapping.options.decimalSeparator;
    const cellOf = (column, value) => {
        if (value === undefined || typeof value === 'number')
            return value ?? null;
        const text = value.trim();
        if (!text)
            return null;
        if (column.type === 'number') {
            return normalizeImportNumber(text, decimalSeparator, weightUnit) ?? text;
        }
        if (column.type === 'date')
            return normalizeImportDate(text, dateOrder, timeZone) ?? text;
        return text;
    };
    const rows = [];
    for (const line of source.rows) {
        const cells = {};
        let blank = true;
        for (const column of template.columns) {
            const at = index[column.key];
            if (at === undefined)
                continue;
            const cell = cellOf(column, line.cells[at]);
            cells[column.key] = cell;
            if (cell !== null)
                blank = false;
        }
        if (!blank)
            rows.push({ row: line.line, cells });
    }
    if (!rows.length && !issues.some((i) => i.severity === 'ERROR')) {
        issues.push({ row: null, code: 'FILE_EMPTY', severity: 'ERROR' });
    }
    if (rows.length > IMPORT_MAX_ROWS) {
        issues.push({
            row: null,
            code: 'FILE_TOO_MANY_ROWS',
            params: { max: IMPORT_MAX_ROWS },
            severity: 'ERROR',
        });
    }
    return { headers, rows, issues };
}
/** The values of the columns that have few of them (200 at most), to map products to wastes. */
export function distinctSourceValues(source, limit = 200) {
    const distinct = {};
    source.headers.forEach((header, i) => {
        const values = new Set();
        for (const row of source.rows) {
            const text = String(row.cells[i] ?? '').trim();
            if (text)
                values.add(text);
            if (values.size > limit)
                return;
        }
        if (values.size)
            distinct[header] = [...values].sort((a, b) => a.localeCompare(b, 'fr'));
    });
    return distinct;
}
//# sourceMappingURL=import-mapping.js.map